import React from 'react';
import { Zap, Layers, Wrench, Building, Home as HomeIcon, Briefcase, Landmark, Factory, CheckCircle2, ArrowRight } from 'lucide-react';
import { PageHero, SectionHeader, Reveal, TechCard, IconBox, BtnPrimary, BtnGlass } from '../components/ui-kit';

const activities = [
  {
    id: 1, icon: Zap, tone: 'energy', title: 'Installations Électriques',
    description: "Réalisation d'installations électriques complètes pour habitations neuves et locaux professionnels.",
    services: ['Tableau électrique', 'Prises et interrupteurs', 'Éclairage intérieur et extérieur', 'Chauffage électrique'],
    image: '/assets/commercial1-tableau.webp'
  },
  {
    id: 2, icon: Wrench, tone: 'signal', title: 'Dépannage & Maintenance',
    description: 'Service de dépannage rapide disponible 7j/7 pour tous vos problèmes électriques urgents.',
    services: ['Diagnostic de panne', 'Réparation urgente', 'Remplacement de composants', 'Maintenance préventive'],
    image: '/assets/projet14.jpg'
  },
  {
    id: 3, icon: Layers, tone: 'energy', title: 'Plâtrerie & Cloisons',
    description: 'Aménagement intérieur avec pose de cloisons sèches, isolation et finitions professionnelles.',
    services: ['Cloisons en plaques de plâtre', 'Doublage et isolation', 'Plafonds suspendus', 'Joints et enduits'],
    image: '/assets/projet16.jpg'
  },
  {
    id: 4, icon: Building, tone: 'signal', title: 'Rénovation & Mise aux Normes',
    description: 'Rénovation complète de vos installations avec mise aux normes NF C 15-100.',
    services: ['Diagnostic électrique', 'Mise aux normes', 'Rénovation complète', 'Modernisation'],
    image: '/assets/commercial3-restaurant2.webp'
  }
];

const sectors = [
  { title: 'Particuliers', description: 'Maisons, appartements, rénovations', icon: HomeIcon },
  { title: 'Professionnels', description: 'Bureaux, commerces, locaux professionnels', icon: Briefcase },
  { title: 'Collectivités', description: 'Établissements publics, écoles', icon: Landmark },
  { title: 'Industrie', description: 'Sites industriels, entrepôts', icon: Factory }
];

const Activities = () => (
  <div className="min-h-screen" data-testid="activities-page">
    <PageHero
      testId="activities-hero"
      eyebrow="Nos activités"
      title={<>Une gamme complète, <span className="text-energy-gradient">du câble à la finition.</span></>}
      subtitle="TSE OUEST intervient dans tous les domaines de l'électricité générale et de la plâtrerie pour particuliers et professionnels."
      image="/assets/commercial5-overhead.webp"
    />

    <section className="bg-[#F8FAFC] text-ink py-24 lg:py-32 circuit-grid-light" data-testid="activities-list">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Domaines d'intervention" title="Quatre pôles d'expertise" />
        <div className="mt-16 space-y-20">
          {activities.map((a, index) => (
            <div key={a.id} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid={`activity-${a.id}`}>
              <Reveal className={`lg:col-span-6 ${index % 2 ? 'lg:order-2' : ''}`}>
                <div className="brackets always rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(11,17,32,0.15)] group">
                  <img src={a.image} alt={a.title} className="w-full h-[380px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
              </Reveal>
              <Reveal delay={120} className={`lg:col-span-6 ${index % 2 ? 'lg:order-1' : ''}`}>
                <div className="flex items-center gap-4">
                  <IconBox icon={a.icon} tone={a.tone} />
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Pôle 0{a.id}</span>
                </div>
                <h3 className="mt-6 font-display text-3xl sm:text-4xl font-black tracking-tight">{a.title}</h3>
                <p className="text-slate-600 mt-5 leading-relaxed text-base md:text-lg">{a.description}</p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {a.services.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl px-4 py-3">
                      <CheckCircle2 size={16} className="text-energy shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="relative bg-obsidian text-white py-24 lg:py-32 overflow-hidden noise" data-testid="activities-sectors">
      <div className="absolute inset-0 dot-matrix opacity-30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader dark eyebrow="Secteurs" title="Nous intervenons auprès de tous types de clients" />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {sectors.map((s, i) => (
            <Reveal key={s.title} delay={i * 80}>
              <TechCard dark className="h-full group">
                <IconBox icon={s.icon} tone={i % 2 ? 'signal' : 'energy'} />
                <h3 className="mt-6 font-display text-xl font-bold">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-2">{s.description}</p>
              </TechCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative bg-abyss text-white py-24 overflow-hidden" data-testid="activities-cta">
      <div className="glow-orb w-[600px] h-[300px] bg-energy/20 bottom-0 left-1/2 -translate-x-1/2" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="glass-dark brackets always rounded-3xl p-10 lg:p-14 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Un projet en tête ?</h2>
          <p className="text-base md:text-lg text-slate-300 mt-5 max-w-2xl mx-auto">Quelle que soit l'ampleur de votre projet, TSE OUEST vous accompagne de A à Z.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <BtnPrimary to="/contact" data-testid="activities-devis-btn">Demander un devis</BtnPrimary>
            <BtnGlass to="/services" data-testid="activities-services-btn">Voir nos services <ArrowRight size={16} /></BtnGlass>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Activities;
